import { createSnapshotValue as createMutable } from '../../lib/snapshot-value.ts'
import type { SectionMaterialsPreview } from '../lib/section-materials.ts'
import type { ProductionPriority, ProtoActivity, ProtoFile, ProtoStageRun } from './orders.ts'

export interface ProtoInternalFile extends ProtoFile {
  downloadUrl: string
}

/**
 * Spec §12.6.1: an order made for the factory itself, with no customer. It records what, how
 * many, and who asked: a warehouse or a work center. It has no material gate.
 */
export interface ProtoInternalOrder {
  programFiles?: ProtoFile[]
  techCardVersion?: number
  id: string
  number: string
  productName: string
  /** Set when the item is a catalog product with a route; free-text items have none. */
  productId: string | null
  quantity: string
  unit: string
  requester: { type: 'warehouse' | 'work_center'; name: string }
  requestedById: string
  /** The administrator's decision on the production plan. Unset until one is made. */
  inPlan?: boolean
  createdAt: string
  deadline: string
  priority: ProductionPriority
  note: string | null
  files: ProtoInternalFile[]
  sectionMaterials: SectionMaterialsPreview | null
  stages: ProtoStageRun[]
  activity: ProtoActivity[]
}

export interface InternalOrderSeed {
  id: string
  number: string
  productName: string
  productId: string | null
  /** Used when there is no catalog product: the stages this one-off job needs. */
  stageKeys?: string[]
  quantity: string
  unit: string
  requester: ProtoInternalOrder['requester']
  requestedById: string
  createdAt: string
  deadline: string
  priority: ProductionPriority
  note: string | null
  stageIndex: number
  goodInStage: string
  /** Leaves the current stage without an operator, so the shop manager has to assign one. */
  activeUnassigned?: boolean
}

export const INTERNAL_ORDER_SEEDS: InternalOrderSeed[] = createMutable([] as InternalOrderSeed[])

/**
 * Spec §12.6: a row added to the monthly plan by hand, for work that has no order in the system.
 * It names a VEEM legal entity, and is archived rather than deleted.
 */
export interface ManualPlanRow {
  id: string
  productName: string
  quantity: string
  unit: string
  /** Gross UAH amount. */
  amount: string
  sellerEntityId: string
  customer: string
  deadline: string
  note: string | null
  createdById: string
  createdAt: string
  archivedAt: string | null
}

export const MANUAL_PLAN_ROWS: ManualPlanRow[] = createMutable([] as ManualPlanRow[])

/** Scrap already recorded before the demo morning, so the «Брак» register has history. */
export const SCRAP_SEED: {
  number: string
  stageKey: string
  at: string
  actorId: string
  scrap: string
  reason: string
  note?: string
}[] = createMutable(
  [] as {
    number: string
    stageKey: string
    at: string
    actorId: string
    scrap: string
    reason: string
    note?: string
  }[],
)

/** A machine taken out of work, with why and since when (spec: blocks starting a task). */
export interface EquipmentRepair {
  reason: string
  since: string
  byId: string
}
